<template>
  <vxe-table
    v-if="isLargeScreen"
    class="history-table"
    empty-text="No data"
    border="none"
    :data="historyDataList"
    :scroll-y="{ enabled: false }"
    :cell-class-name="cellClassName"
    @cell-click="clickCellEvent"
  >
    <vxe-column
      type="html"
      field="timestamp"
      title="Timestamp"
      min-width="192px"
      width="30%"
      sortable
      sort-by="timestamp"
      :formatter="formatDate"
    ></vxe-column>
    <vxe-column
      field="filter"
      title="Filter"
      min-width="80px"
      width="15%"
      sortable
      sort-by="filter"
    ></vxe-column>
    <vxe-column
      type="html"
      field="trigger"
      title="Trigger"
      min-width="128px"
      width="25%"
      sortable
      sort-by="trigger"
      :formatter="formatTrigger"
    ></vxe-column>
    <vxe-column
      type="html"
      field="reason"
      title="Incident"
      min-width="256px"
      width="30%"
      sortable
      sort-by="reason"
      :formatter="formatReason"
    ></vxe-column>
  </vxe-table>

  <div v-else class="mobile">
    <div v-if="!historyDataList || !historyDataList.length" class="no-data">
      <p>No data</p>
    </div>
    <div v-else>
      <div class="icon-group-and-search">
        <div class="icon-group">
          <fa
            icon="sort-amount-down"
            class="icon"
            :class="{ active: isShowSortTool }"
            @click="toggleIsShowSortTool"
          ></fa>
          <fa
            v-if="false"
            icon="filter"
            class="icon"
            :class="{ active: isShowFilterTool }"
            @click="toggleIsShowFilterTool"
          ></fa>
        </div>
      </div>
      <div v-if="isShowSortTool" class="tool">
        <SortAndFilterToolAlertsRS
          :toolConfig="sortToolConfig"
          @onToolChange="handleSortToolChange"
        ></SortAndFilterToolAlertsRS>
      </div>
      <div v-if="isShowFilterTool" class="tool">
        <SortAndFilterToolAlertsRS
          :toolConfig="filterToolConfig"
          @onToolChange="handleFilterToolChange"
        ></SortAndFilterToolAlertsRS>
      </div>
    </div>
    <ul>
      <li v-for="history in processedHistoryDataList" :key="history.timestamp">
        <div class="date">
          <span>Timestamp</span>
          <span>{{ history.timestamp | timestampFormatter }}</span>
        </div>
        <div class="filter">
          <span>Filter</span>
          <span>{{ history.filter }}</span>
        </div>
        <div class="trigger">
          <span>Trigger</span>
          <template v-if="history.isTriggerSliced">
            <span>
              {{ history.slicedTrigger }} <br />
              <span
                class="show-more"
                @click="
                  setShowMoreModalObj({
                    isShown: true,
                    bodyTextOfTriggers: history.trigger,
                    bodyTextOfIncident: history.reason,
                    modalType: SHOW_MORE_MODAL_TYPE.TRIGGERS,
                    alertId: history.alertId
                  })
                "
              >
                and {{ history.moreTriggerAmount }} more
              </span>
            </span>
          </template>
          <template v-else>
            <span>{{ history.trigger }}</span>
          </template>
        </div>
        <div class="reason">
          <span>Incident</span>
          <template v-if="history.isReasonSliced">
            <span>
              {{ history.slicedReason }} <br />
              <span
                class="show-more"
                @click="
                  setShowMoreModalObj({
                    isShown: true,
                    bodyTextOfTriggers: history.trigger,
                    bodyTextOfIncident: history.reason,
                    modalType: SHOW_MORE_MODAL_TYPE.INCIDENT,
                    alertId: history.alertId
                  })
                "
              >
                ...and more</span
              >
            </span>
          </template>
          <template v-else>
            <span>{{ history.reason }}</span>
          </template>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapGetters, mapMutations } from 'vuex';
import {
  HISTORY_SORT_OPTION,
  HISTORY_FILTER_OPTION,
  SHOW_MORE_MODAL_TYPE
} from '../../../../constants/alertsRoutingStatusConstant';
import { formatDateAsLocalTimestamp } from '../../../../utils/common';
import SortAndFilterToolAlertsRS from '../alertTableParts/SortAndFilterToolAlertsRS.vue';

export default {
  name: 'AlertRSHistoryTable',
  components: { SortAndFilterToolAlertsRS },
  props: ['historyDataList'],
  data() {
    return {
      isShowSortTool: false,
      isShowFilterTool: false,
      sortType: 0,
      filterType: 0,
      sortToolConfig: {
        placeholder: 'Sort',
        toolList: [
          { id: HISTORY_SORT_OPTION.NONE, text: 'None' },
          {
            id: HISTORY_SORT_OPTION.NEWEST_FIRST,
            text: 'Newest first'
          },
          {
            id: HISTORY_SORT_OPTION.OLDEST_FIRST,
            text: 'Oldest first'
          },
          {
            id: HISTORY_SORT_OPTION.TRIGGER_ASCENDING,
            text: 'Trigger ascending'
          },
          {
            id: HISTORY_SORT_OPTION.TRIGGER_DESCENDING,
            text: 'Trigger descending'
          },
          {
            id: HISTORY_SORT_OPTION.REASON_ASCENDING,
            text: 'Incident ascending'
          },
          {
            id: HISTORY_SORT_OPTION.REASON_ASCENDING,
            text: 'Incident descending'
          }
        ]
      },
      filterToolConfig: {
        placeholder: 'Filter',
        toolList: [{ id: HISTORY_FILTER_OPTION.NONE, text: 'None' }]
      },
      SHOW_MORE_MODAL_TYPE: SHOW_MORE_MODAL_TYPE
    };
  },
  computed: {
    ...mapGetters(['isLargeScreen']),
    processedHistoryDataList() {
      let list = [];

      switch (this.filterType) {
        case HISTORY_FILTER_OPTION.NONE:
          list = this.historyDataList;
          break;

        default:
          list = this.historyDataList;
          break;
      }

      // deep clone list as list.sort will change the origin list
      list = JSON.parse(JSON.stringify(list));

      switch (this.sortType) {
        case HISTORY_SORT_OPTION.NONE:
          break;
        case HISTORY_SORT_OPTION.NEWEST_FIRST:
          list.sort((a, b) => dayjs(b.timestamp) - dayjs(a.timestamp));
          break;
        case HISTORY_SORT_OPTION.OLDEST_FIRST:
          list.sort((a, b) => dayjs(a.timestamp) - dayjs(b.timestamp));
          break;
        case HISTORY_SORT_OPTION.REASON_DESCENDING:
          list.sort((a, b) => {
            const reasonA = a.reason || '';
            const reasonB = b.reason || '';
            if (reasonB < reasonA) return -1;
            if (reasonB > reasonA) return 1;

            return 0;
          });
          break;
        case HISTORY_SORT_OPTION.REASON_ASCENDING:
          list.sort((a, b) => {
            const reasonA = a.reason || '';
            const reasonB = b.reason || '';
            if (reasonB < reasonA) return 1;
            if (reasonB > reasonA) return -1;

            return 0;
          });
          break;
        case HISTORY_SORT_OPTION.TRIGGER_DESCENDING:
          list.sort((a, b) => {
            const triggerA = a.trigger || '';
            const triggerB = b.trigger || '';
            if (triggerB < triggerA) return -1;
            if (triggerB > triggerA) return 1;

            return 0;
          });
          break;
        case HISTORY_SORT_OPTION.TRIGGER_ASCENDING:
          list.sort((a, b) => {
            const triggerA = a.trigger || '';
            const triggerB = b.trigger || '';
            if (triggerB < triggerA) return 1;
            if (triggerB > triggerA) return -1;

            return 0;
          });
          break;
        default:
          break;
      }

      return list;
    }
  },
  methods: {
    ...mapMutations('alertsRoutingStatusStore', ['setShowMoreModalObj']),

    formatDate({ cellValue }) {
      return formatDateAsLocalTimestamp(cellValue);
    },

    toggleIsShowSortTool() {
      this.isShowSortTool = !this.isShowSortTool;
      if (!this.isShowSortTool) this.sortType = HISTORY_SORT_OPTION.NONE;
    },
    toggleIsShowFilterTool() {
      this.isShowFilterTool = !this.isShowFilterTool;
      if (!this.isShowFilterTool) this.filterType = HISTORY_FILTER_OPTION.NONE;
    },
    handleSortToolChange(val) {
      this.sortType = val;
    },
    handleFilterToolChange(val) {
      this.filterType = val;
    },

    // Add class name "word-break--normal" to cells in Incident column
    cellClassName({ row, rowIndex, column, columnIndex }) {
      if (column.property === 'reason' || column.property === 'trigger') {
        return 'word-break--normal';
      }
    },

    formatTrigger({ row }) {
      const {
        isTriggerSliced,
        slicedTrigger,
        moreTriggerAmount,
        trigger
      } = row;

      if (!isTriggerSliced) return trigger;

      return `<span class="pointer">${slicedTrigger} <br /><span class="show-more">and ${moreTriggerAmount} more</span></span>`;
    },

    formatReason({ row }) {
      const { isReasonSliced, slicedReason, reason } = row;

      if (!isReasonSliced) return reason;

      return `<span class="pointer">${slicedReason} <br /><span class="show-more"> ...and more</span></span>`;
    },

    clickCellEvent(event) {
      const { column, row } = event;

      if (column.field === 'trigger' && row.isTriggerSliced) {
        this.setShowMoreModalObj({
          isShown: true,
          bodyTextOfTriggers: row.trigger,
          bodyTextOfIncident: row.reason,
          modalType: SHOW_MORE_MODAL_TYPE.TRIGGERS,
          alertId: row.alertId
        });
      }

      if (column.field === 'reason' && row.isReasonSliced) {
        this.setShowMoreModalObj({
          isShown: true,
          bodyTextOfTriggers: row.trigger,
          bodyTextOfIncident: row.reason,
          modalType: SHOW_MORE_MODAL_TYPE.INCIDENT,
          alertId: row.alertId
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.history-table {
  ::v-deep .vxe-header--row {
    background-color: #ffffff !important;
  }

  ::v-deep td.vxe-body--column {
    background-image: none;
  }

  ::v-deep .show-more {
    color: $apnic-blue;
    cursor: pointer;
  }

  ::v-deep .pointer {
    cursor: pointer;
  }
}

.mobile {
  .no-data {
    padding: 1rem 0;
  }

  ul {
    list-style: none;
    padding-bottom: 1.5rem;
  }

  li {
    border-bottom: 1px solid #666;
    margin-top: 0.5rem;
    padding-top: 1rem;

    &:nth-child(1) {
      border-top: 1px solid #666;
    }
  }

  .date,
  .reason,
  .trigger,
  .filter {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    margin-bottom: 1.25rem;
  }

  .date span:nth-child(1),
  .filter span:nth-child(1),
  .reason span:nth-child(1),
  .trigger span:nth-child(1) {
    color: $text-black;
    font-weight: 600;
    width: 6.5rem;
    min-width: 6.5rem;
  }

  .date span:nth-child(2),
  .filter span:nth-child(2),
  .reason span:nth-child(2),
  .trigger span:nth-child(1) {
    text-align: left;
  }

  .icon-group-and-search {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;

    .icon {
      padding: 0.5rem;
      margin-right: 0.5rem;
      width: 2rem;
      height: 2rem;
      color: $icon-grey;
    }

    .active {
      color: $text-light-blue;
    }
  }

  .tool {
    padding-bottom: 0.5rem;
  }

  .show-more {
    color: $apnic-blue;
    cursor: pointer;
  }
}
</style>
